import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import { KeyboardArrowDown, KeyboardArrowRight, QueryStats } from "@mui/icons-material";
import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { RangeType, useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../components/common/empty-placeholder";
import { useFormatDateHumanReadable } from "../../../../hooks";
import { formatDateHumanReadable } from "../../../../utils";
import { IShortTermPredictItem } from "../scheme/components/state-and-forecast/interfaces";
import { ForecastResultsPanel } from "./forecast-results-panel";
import {
  IAnomalyChronogramData,
  IAnomalyChronogramItem,
  IAnomalyChronogramNode,
  IAnomalyMeasureTrendData,
  IRulPredictItem,
  ISelectedForecastTrend,
} from "./interfaces";
import { SelectedTrendsSection } from "./selected-measure-trend";
import {
  applyPinnedTrainTciTrend,
  buildChronogramTicks,
  buildForecastWeekStartTimestamp,
  buildTciTrendRequest,
  CHRONOGRAM_NAME_CELL_WIDTH,
  createDefaultTrainTciTrend,
  createEmptyAnomalyMeasureTrendData,
  flattenVisibleChronogramRows,
  FORECAST_EXTENSION_BOUNDARY_COLOR,
  FORECAST_TIMELINE_GRID_SX,
  FORECAST_TIMELINE_PLOT_CELL_SX,
  getAnomalyWeightColor,
  getChronogramItemOffset,
  getForecastTimestampOffset,
  hasTrendLineData,
  isTciForecastTrend,
  normalizeAnomalyChronogramData,
  normalizeAnomalyMeasureTrendData,
  normalizeRulPredictItems,
  normalizeTciTrendData,
  resolveForecastTrendRefresh,
  toggleSelectedForecastTrend,
} from "./utils";
import { ANOMALY_TIMELINE_BACKGROUND } from "../scheme/components/state-and-forecast/constants";

interface IForecastTrendSelectionState {
  trainId: number | null;
  trends: ISelectedForecastTrend[];
}

interface IForecastRangeControls {
  recorderModeInterval: number;
  trainId: number | null;
  userSelectedRangeType: RangeType;
  userSelectedRecorderModePeriod: string;
}

const EMPTY_SELECTED_FORECAST_TRENDS: ISelectedForecastTrend[] = [];

const normalizeTrainId = (value: unknown): number | null => {
  const id = Number(value);

  return Number.isFinite(id) && id > 0 ? id : null;
};

const ChronogramStrip: FC<{
  endTimestamp: string | Date;
  formatNumber: IntlShape["formatNumber"];
  items: IAnomalyChronogramItem[];
  startTimestamp: string | Date;
  extensionBoundaryTimestamp: string | Date | null;
}> = ({ endTimestamp, extensionBoundaryTimestamp, formatNumber, items, startTimestamp }) => {
  const extensionBoundaryOffset = extensionBoundaryTimestamp
    ? getForecastTimestampOffset(extensionBoundaryTimestamp, startTimestamp, endTimestamp)
    : null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 34,
        borderRadius: 0.75,
        overflow: "hidden",
        backgroundColor: ANOMALY_TIMELINE_BACKGROUND,
        border: "1px solid rgba(10, 42, 12, 0.35)",
      }}
    >
      {items.map((item, index) => {
        const offset = getChronogramItemOffset(item, startTimestamp, endTimestamp);

        if (offset === null) {
          return null;
        }

        return (
          <Tooltip
            key={`${item.Date}-${item.Value}-${index}`}
            title={`${formatDateHumanReadable(item.Date)} - ${formatNumber(item.Value, {
              maximumFractionDigits: 3,
              minimumFractionDigits: 0,
            })}`}
            arrow
            placement="top"
            disableInteractive
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `clamp(0px, calc(${offset}% - 2px), calc(100% - 4px))`,
                width: 4,
                minWidth: 4,
                backgroundColor: getAnomalyWeightColor(item.Value),
                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.08)",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        );
      })}
      {extensionBoundaryOffset !== null ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `calc(${extensionBoundaryOffset}% - 1px)`,
            width: 2,
            backgroundColor: FORECAST_EXTENSION_BOUNDARY_COLOR,
            boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.65)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
      ) : null}
    </Box>
  );
};

const ChronogramTable: FC<{
  endTimestamp: string | Date;
  extensionBoundaryTimestamp: string | Date | null;
  expandedKeys: string[];
  formatNumber: IntlShape["formatNumber"];
  onTrendToggle: (node: IAnomalyChronogramNode) => void;
  onToggle: (key: string) => void;
  isTrainTciTrendPinned: boolean;
  root: IAnomalyChronogramNode;
  selectedTrendKeys: string[];
  startTimestamp: string | Date;
}> = ({
  endTimestamp,
  extensionBoundaryTimestamp,
  expandedKeys,
  formatNumber,
  onTrendToggle,
  onToggle,
  isTrainTciTrendPinned,
  root,
  selectedTrendKeys,
  startTimestamp,
}) => {
  const { formatMessage } = useIntl();
  const rows = flattenVisibleChronogramRows(root, expandedKeys);
  const ticks = buildChronogramTicks(startTimestamp, endTimestamp);
  const extensionBoundaryOffset = extensionBoundaryTimestamp
    ? getForecastTimestampOffset(extensionBoundaryTimestamp, startTimestamp, endTimestamp)
    : null;

  return (
    <Box
      sx={{
        border: "1px solid #d7dde9",
        borderRadius: 0,
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          backgroundColor: "#0f3313",
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        }}
      >
        <Typography sx={{ color: "#ffffff", fontSize: 16, lineHeight: "20px", fontWeight: 600 }}>
          <FormattedMessage id="pages.forecasting.anomalyChronogramTitle" />
        </Typography>
      </Box>
      <Box>
        {rows.map(({ depth, key, node }) => {
          const isExpanded = expandedKeys.includes(key);
          const isExpandable = node.children.length > 0;
          const isPinnedTrainTci = isTrainTciTrendPinned && node.objectType === "train";
          const isTrendRow = node.objectType !== "point";
          const isSelectable = isTrendRow && !isPinnedTrainTci;
          const isSelected = isTrendRow && selectedTrendKeys.includes(key);
          const nodeLabel = (
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: "16px",
                fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500,
                color: isSelected ? "#173a8a" : "#202534",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={node.name}
            >
              {node.name}
            </Typography>
          );

          return (
            <Box
              key={key}
              onClick={() => {
                if (isSelectable) {
                  onTrendToggle(node);
                }
              }}
              sx={{
                ...FORECAST_TIMELINE_GRID_SX,
                cursor: isSelectable ? "pointer" : "default",
                borderBottom: "1px solid #e4e8f1",
                backgroundColor: isSelected ? "rgba(47, 128, 237, 0.06)" : undefined,
                "&:hover": isSelectable ? { backgroundColor: "rgba(47, 128, 237, 0.04)" } : undefined,
              }}
            >
              <Box
                sx={{
                  width: CHRONOGRAM_NAME_CELL_WIDTH,
                  py: 1,
                  px: 1,
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  backgroundColor: isSelected ? "rgba(47, 128, 237, 0.1)" : undefined,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, width: "100%", pl: depth * 2 }}>
                  {isExpandable ? (
                    <IconButton
                      size="small"
                      aria-expanded={isExpanded}
                      aria-label={formatMessage(
                        {
                          id: isExpanded
                            ? "pages.forecasting.collapseChronogramRow"
                            : "pages.forecasting.expandChronogramRow",
                        },
                        { name: node.name },
                      )}
                      onClick={event => {
                        event.stopPropagation();
                        onToggle(key);
                      }}
                      onKeyDown={event => event.stopPropagation()}
                      sx={{ mr: 0.5 }}
                    >
                      {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 32, flexShrink: 0, mr: 0.5 }} />
                  )}
                  {isTrendRow ? (
                    <ButtonBase
                      aria-disabled={isPinnedTrainTci || undefined}
                      aria-pressed={isSelected}
                      disabled={isPinnedTrainTci}
                      onClick={event => {
                        event.stopPropagation();

                        if (isSelectable) {
                          onTrendToggle(node);
                        }
                      }}
                      sx={{
                        minWidth: 0,
                        flexGrow: 1,
                        justifyContent: "flex-start",
                        textAlign: "left",
                      }}
                    >
                      {nodeLabel}
                    </ButtonBase>
                  ) : (
                    nodeLabel
                  )}
                </Box>
              </Box>
              <Box sx={{ ...FORECAST_TIMELINE_PLOT_CELL_SX, py: 1 }}>
                <ChronogramStrip
                  endTimestamp={endTimestamp}
                  extensionBoundaryTimestamp={extensionBoundaryTimestamp}
                  formatNumber={formatNumber}
                  items={node.items}
                  startTimestamp={startTimestamp}
                />
              </Box>
            </Box>
          );
        })}
        <Box sx={FORECAST_TIMELINE_GRID_SX}>
          <Box sx={{ width: CHRONOGRAM_NAME_CELL_WIDTH, px: 1, pt: 1.5 }}>
            <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472" }}>
              {formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.date" })}
            </Typography>
          </Box>
          <Box sx={{ ...FORECAST_TIMELINE_PLOT_CELL_SX, pt: 1.5, pb: 1 }}>
            <Box sx={{ position: "relative", height: 26 }}>
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 16,
                  borderTop: "1px solid #c8cedd",
                }}
              />
              {ticks.map((tick, i) => (
                <Box
                  key={tick.key}
                  sx={{
                    position: "absolute",
                    left: `${tick.left}%`,
                    top: 0,
                    transform: "translateX(-50%)",
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ width: 1.1, height: 20, backgroundColor: "#c8cedd", mx: "auto", mt: -0.5 }} />
                  <Typography
                    sx={{
                      mt: 0.5,
                      mr: i + 1 === ticks.length ? 5 : 0,
                      fontSize: 11,
                      lineHeight: "13px",
                      color: "#5f6472",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tick.label}
                  </Typography>
                </Box>
              ))}
              {extensionBoundaryOffset !== null ? (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    left: `calc(${extensionBoundaryOffset}% - 1px)`,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    backgroundColor: FORECAST_EXTENSION_BOUNDARY_COLOR,
                    pointerEvents: "none",
                    zIndex: 2,
                  }}
                />
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const ForecastingContent: FC<{
  extensionBoundaryTimestamp: string | Date | null;
  isTrainTciTrendPinned: boolean;
  timelineEndTimestamp: string | Date;
}> = ({ extensionBoundaryTimestamp, isTrainTciTrendPinned, timelineEndTimestamp }) => {
  const [searchParams] = useSearchParams();
  const {
    selectedItem,
    endTimestamp,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
    dbTz,
  } = useAsu();
  const { formatMessage, formatNumber } = useIntl();
  const formatDate = useFormatDateHumanReadable();

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [chronogramData, setChronogramData] = useState<IAnomalyChronogramData>({ root: null });
  const [trendSelection, setTrendSelection] = useState<IForecastTrendSelectionState>({
    trainId: null,
    trends: EMPTY_SELECTED_FORECAST_TRENDS,
  });
  const [trendDataByKey, setTrendDataByKey] = useState<Record<string, IAnomalyMeasureTrendData>>({});
  const [combineTciTrendsInOneChart, setCombineTciTrendsInOneChart] = useState(false);
  const [combineAllTrendsInOneChart, setCombineAllTrendsInOneChart] = useState(false);
  const [shortTermPredictItems, setShortTermPredictItems] = useState<IShortTermPredictItem[]>([]);
  const [rulPredictItems, setRulPredictItems] = useState<IRulPredictItem[]>([]);
  const [isTrendLoading, setIsTrendLoading] = useState(false);
  const [resolvedChronogramTrainId, setResolvedChronogramTrainId] = useState<number | null>(null);
  const activeTrainIdRef = useRef<number | null>(null);
  const previousTrendEndTimestampRef = useRef<string | Date>(endTimestamp);
  const pendingExplicitRangeChangeRef = useRef(false);
  const currentRangeTypeRef = useRef(userSelectedRangeType);
  const latestEndTimestampRef = useRef<string | Date>(endTimestamp);
  latestEndTimestampRef.current = endTimestamp;

  const selectedTrainId = selectedItem && "TrainId" in selectedItem ? normalizeTrainId(selectedItem.TrainId) : null;
  const trainId = selectedTrainId ?? normalizeTrainId(searchParams.get("trainId"));
  const previousForecastRangeControlsRef = useRef<IForecastRangeControls>({
    recorderModeInterval,
    trainId,
    userSelectedRangeType,
    userSelectedRecorderModePeriod,
  });
  const selectedTrainName =
    selectedItem &&
    "TrainId" in selectedItem &&
    normalizeTrainId(selectedItem.TrainId) === trainId &&
    typeof selectedItem.TrainName === "string" &&
    selectedItem.TrainName.trim()
      ? selectedItem.TrainName.trim()
      : formatMessage({ id: "pages.forecasting.trainFallback" }, { id: trainId ?? "" });
  const pinnedTrainTciTrend = useMemo(
    () => (trainId && isTrainTciTrendPinned ? createDefaultTrainTciTrend(trainId, selectedTrainName) : null),
    [isTrainTciTrendPinned, trainId, selectedTrainName],
  );
  const manuallySelectedTrends =
    trendSelection.trainId === trainId ? trendSelection.trends : EMPTY_SELECTED_FORECAST_TRENDS;
  const selectedTrends = useMemo(
    () => applyPinnedTrainTciTrend(manuallySelectedTrends, pinnedTrainTciTrend),
    [manuallySelectedTrends, pinnedTrainTciTrend],
  );
  const forecastStartTimestamp = buildForecastWeekStartTimestamp(endTimestamp);
  const currentChronogramRoot =
    chronogramData.root?.objectType === "train" && chronogramData.root.id === trainId ? chronogramData.root : null;
  const chronogramRoot =
    currentChronogramRoot ??
    (trainId
      ? {
          objectType: "train" as const,
          id: trainId,
          name: selectedTrainName,
          items: [],
          children: [],
        }
      : null);

  const { fn: getAnomalyChronogram, isLoading: isAnomalyChronogramLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/anomaly-chronogram/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as IAnomalyChronogramData | null;

        return normalizeAnomalyChronogramData(json, dbTz);
      } catch (error) {
        console.error(error);
        return { root: null };
      }
    },
    { success: false, error: false },
  );

  const { fn: getShortTermPredict, isLoading: isShortTermPredictLoading } = useApiCall(
    async (api, { idTrain }: { idTrain: number }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/short-term-forecast/${idTrain}`,
          method: "GET",
        })) as { items?: IShortTermPredictItem[] } | null;

        return Array.isArray(json?.items) ? json.items : [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    { success: false, error: false },
  );

  const { fn: getRulPredict, isLoading: isRulPredictLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/rul/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as { items?: IRulPredictItem[] } | null;

        return normalizeRulPredictItems(json?.items);
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    { success: false, error: false },
  );

  const { fn: getMeasureTrend } = useApiCall(
    async (api, { idMeasure, startTs, endTs }: { idMeasure: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/measure-trend/${idMeasure}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as IAnomalyMeasureTrendData | null;

        return normalizeAnomalyMeasureTrendData(json, dbTz);
      } catch (error) {
        console.error(error);
        return createEmptyAnomalyMeasureTrendData();
      }
    },
    { success: false, error: false },
  );

  const { fn: getTciTrend } = useApiCall(
    async (
      api,
      { trend, startTs, endTs }: { trend: ISelectedForecastTrend; startTs: string | Date; endTs: string | Date },
    ) => {
      const request = buildTciTrendRequest(trend, startTs, endTs);

      if (!request) {
        return createEmptyAnomalyMeasureTrendData();
      }

      const json = (await api.fetchJson({
        url: request.url,
        method: "GET",
        data: request.data,
      })) as Pick<IAnomalyMeasureTrendData, "history" | "forecast"> | null;

      return json ? normalizeTciTrendData(json, dbTz) : null;
    },
    { success: false },
  );

  useEffect(() => {
    const isNewTrain = activeTrainIdRef.current !== trainId;
    activeTrainIdRef.current = trainId;

    if (isNewTrain) {
      setTrendSelection({ trainId, trends: EMPTY_SELECTED_FORECAST_TRENDS });
      setCombineTciTrendsInOneChart(false);
      setCombineAllTrendsInOneChart(false);
      return;
    }

    setTrendSelection(current => {
      if (current.trainId !== trainId) {
        return { trainId, trends: EMPTY_SELECTED_FORECAST_TRENDS };
      }

      if (!trainId) {
        return current;
      }

      const trends = current.trends.map(trend =>
        trend.objectType === "train" && trend.id === trainId && trend.name !== selectedTrainName
          ? { ...trend, name: selectedTrainName }
          : trend,
      );

      return trends.some((trend, index) => trend !== current.trends[index]) ? { ...current, trends } : current;
    });
  }, [trainId, selectedTrainName]);

  useEffect(() => {
    setExpandedKeys([]);
    setChronogramData({ root: null });
    setResolvedChronogramTrainId(null);
    setTrendDataByKey({});
    setShortTermPredictItems([]);
    setRulPredictItems([]);
    setIsTrendLoading(false);
  }, [trainId]);

  useEffect(() => {
    const nextRangeControls: IForecastRangeControls = {
      recorderModeInterval,
      trainId,
      userSelectedRangeType,
      userSelectedRecorderModePeriod,
    };
    const previousRangeControls = previousForecastRangeControlsRef.current;
    const hasTrainChanged = previousRangeControls.trainId !== nextRangeControls.trainId;
    const hasExplicitRangeChange =
      previousRangeControls.recorderModeInterval !== nextRangeControls.recorderModeInterval ||
      previousRangeControls.userSelectedRangeType !== nextRangeControls.userSelectedRangeType ||
      previousRangeControls.userSelectedRecorderModePeriod !== nextRangeControls.userSelectedRecorderModePeriod;

    currentRangeTypeRef.current = userSelectedRangeType;
    if (hasTrainChanged) {
      pendingExplicitRangeChangeRef.current = false;
      previousTrendEndTimestampRef.current = latestEndTimestampRef.current;
    } else if (hasExplicitRangeChange) {
      pendingExplicitRangeChangeRef.current = userSelectedRangeType !== RangeType.SELECTED_RANGE;
    }
    previousForecastRangeControlsRef.current = nextRangeControls;
  }, [trainId, userSelectedRangeType, recorderModeInterval, userSelectedRecorderModePeriod]);

  const handleTrendToggle = (node: IAnomalyChronogramNode) =>
    setTrendSelection(current => ({
      trainId,
      trends: toggleSelectedForecastTrend(
        current.trainId === trainId ? current.trends : EMPTY_SELECTED_FORECAST_TRENDS,
        node,
      ),
    }));

  useEffect(() => {
    if (!trainId || !forecastStartTimestamp) {
      setResolvedChronogramTrainId(null);
      return;
    }

    let isCurrentRequest = true;
    setResolvedChronogramTrainId(null);
    const chronogramRequest = getAnomalyChronogram(undefined, {
      idTrain: trainId,
      startTs: forecastStartTimestamp,
      endTs: timelineEndTimestamp,
    });

    void Promise.allSettled([chronogramRequest]).then(([chronogramResult]) => {
      if (!isCurrentRequest) {
        return;
      }

      const nextChronogramData =
        chronogramResult.status === "fulfilled" ? chronogramResult.value ?? { root: null } : { root: null };

      setChronogramData(nextChronogramData);
      setResolvedChronogramTrainId(trainId);
      setExpandedKeys([]);

      const chronogramRoot = nextChronogramData.root;

      if (chronogramRoot) {
        setTrendSelection(current => {
          if (current.trainId !== trainId) {
            return current;
          }

          const selectedTrainTrend = current.trends.find(
            trend => trend.objectType === "train" && trend.id === chronogramRoot.id,
          );

          if (!selectedTrainTrend || selectedTrainTrend.name === chronogramRoot.name) {
            return current;
          }

          return {
            ...current,
            trends: current.trends.map(trend =>
              trend.key === selectedTrainTrend.key ? { ...trend, name: chronogramRoot.name } : trend,
            ),
          };
        });
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [trainId, timelineEndTimestamp, forecastStartTimestamp, dbTz]);

  useEffect(() => {
    if (!trainId) {
      return;
    }

    let isCurrentRequest = true;

    void getShortTermPredict(undefined, { idTrain: trainId }).then(result => {
      if (isCurrentRequest) {
        setShortTermPredictItems(Array.isArray(result) ? result : []);
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [trainId]);

  useEffect(() => {
    if (!trainId || !forecastStartTimestamp) {
      return;
    }

    let isCurrentRequest = true;

    void getRulPredict(undefined, {
      idTrain: trainId,
      startTs: forecastStartTimestamp,
      endTs: endTimestamp,
    }).then(result => {
      if (isCurrentRequest) {
        setRulPredictItems(Array.isArray(result) ? result : []);
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [trainId, endTimestamp, forecastStartTimestamp]);

  useEffect(() => {
    const refreshDecision = resolveForecastTrendRefresh({
      hasManualTrends: manuallySelectedTrends.length > 0,
      hasPendingExplicitRangeChange: pendingExplicitRangeChangeRef.current,
      isRecorderMode: currentRangeTypeRef.current === RangeType.RECORDER_MODE,
      nextEndTimestamp: endTimestamp,
      previousEndTimestamp: previousTrendEndTimestampRef.current,
    });

    previousTrendEndTimestampRef.current = endTimestamp;
    if (refreshDecision.consumePendingRangeChange) {
      pendingExplicitRangeChangeRef.current = false;
    }

    if (refreshDecision.clearManualTrends) {
      setTrendSelection(current =>
        current.trainId === trainId && current.trends.length > 0
          ? { trainId, trends: EMPTY_SELECTED_FORECAST_TRENDS }
          : current,
      );
      setTrendDataByKey({});
      setIsTrendLoading(false);
      return;
    }

    if (!refreshDecision.shouldLoad) {
      setIsTrendLoading(false);
      return;
    }

    if (!forecastStartTimestamp || selectedTrends.length === 0) {
      setTrendDataByKey({});
      setIsTrendLoading(false);
      return;
    }

    let isCurrentRequest = true;
    setIsTrendLoading(true);

    void Promise.all(
      selectedTrends.map(async trend => ({
        trend,
        data:
          trend.objectType === "measure"
            ? await getMeasureTrend(undefined, {
                idMeasure: trend.id,
                startTs: forecastStartTimestamp,
                endTs: timelineEndTimestamp,
              })
            : await getTciTrend(undefined, {
                trend,
                startTs: forecastStartTimestamp,
                endTs: timelineEndTimestamp,
              }),
      })),
    )
      .then(results => {
        if (!isCurrentRequest) {
          return;
        }

        const nextTrendData: Record<string, IAnomalyMeasureTrendData> = {};
        let hasEmptyTciTrend = false;

        results.forEach(({ trend, data }) => {
          if (isTciForecastTrend(trend)) {
            if (data && !hasTrendLineData(data)) {
              hasEmptyTciTrend = true;
            } else if (data) {
              nextTrendData[trend.key] = data;
            }

            return;
          }

          nextTrendData[trend.key] = data ?? createEmptyAnomalyMeasureTrendData();
        });

        setTrendDataByKey(nextTrendData);

        if (hasEmptyTciTrend) {
          enqueueSnackbar(formatMessage({ id: "pages.forecasting.tciTrendEmptyWarning" }), {
            variant: "warning",
            preventDuplicate: true,
          });
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsTrendLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    selectedTrends,
    manuallySelectedTrends,
    timelineEndTimestamp,
    forecastStartTimestamp,
    endTimestamp,
    trainId,
    dbTz,
  ]);

  const loading = isAnomalyChronogramLoading || isShortTermPredictLoading || isRulPredictLoading || isTrendLoading;

  if (!trainId) {
    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
        <EmptyPlaceholder
          icon={<QueryStats />}
          text={formatMessage({ id: "pages.forecasting.stationPlaceholder" })}
          wrapperProps={{ sx: { minHeight: "100%", backgroundColor: "#fafaff" } }}
        />
      </Box>
    );
  }

  return (
    <ProgressOverlay
      isLoading={loading}
      wrapperSx={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: "#fff",
          pb: 1.5,
        }}
      >
        <ForecastResultsPanel
          formatDate={formatDate}
          formatNumber={formatNumber}
          intervalEndTimestamp={endTimestamp}
          isRulLoading={isRulPredictLoading}
          isShortTermLoading={isShortTermPredictLoading}
          rulItems={rulPredictItems}
          shortTermItems={shortTermPredictItems}
        />
        <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.25 }}>
          <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472", mb: 0.75 }}>
            <FormattedMessage id="pages.scheme.stateAndForecast.anomaliesHint" />
          </Typography>
          {resolvedChronogramTrainId === trainId && !currentChronogramRoot ? (
            <Typography
              role="status"
              sx={{ mb: 0.75, px: 1, py: 0.75, fontSize: 12, lineHeight: "16px", color: "#5f6472" }}
            >
              <FormattedMessage id="pages.forecasting.anomalyChronogramEmpty" />
            </Typography>
          ) : null}
          {chronogramRoot && forecastStartTimestamp ? (
            <ChronogramTable
              endTimestamp={timelineEndTimestamp}
              extensionBoundaryTimestamp={extensionBoundaryTimestamp}
              expandedKeys={expandedKeys}
              formatNumber={formatNumber}
              onTrendToggle={handleTrendToggle}
              onToggle={key => {
                setExpandedKeys(current =>
                  current.includes(key) ? current.filter(item => item !== key) : [...current, key],
                );
              }}
              isTrainTciTrendPinned={isTrainTciTrendPinned}
              root={chronogramRoot}
              selectedTrendKeys={selectedTrends.map(trend => trend.key)}
              startTimestamp={forecastStartTimestamp}
            />
          ) : null}
          {forecastStartTimestamp ? (
            <SelectedTrendsSection
              combineAllTrendsInOneChart={combineAllTrendsInOneChart}
              combineTciTrendsInOneChart={combineTciTrendsInOneChart}
              endTimestamp={timelineEndTimestamp}
              extensionBoundaryTimestamp={extensionBoundaryTimestamp}
              formatDate={formatDateHumanReadable}
              formatNumber={formatNumber}
              isTrainTciTrendPinned={isTrainTciTrendPinned}
              trendDataByKey={trendDataByKey}
              onCombineAllTrendsInOneChartChange={setCombineAllTrendsInOneChart}
              onCombineTciTrendsInOneChartChange={setCombineTciTrendsInOneChart}
              selectedTrends={selectedTrends}
              startTimestamp={forecastStartTimestamp}
            />
          ) : null}
        </Box>
      </Box>
    </ProgressOverlay>
  );
};
